import type { DecoratedCardVariant } from '../DecoratedCard/DecoratedCard';
import { PersonCard } from '../PersonCard/PersonCard';
import { type ParticipantDecorationVariant } from '../PersonCard/ParticipantDecoration';
import '../DecoratedCard/DecoratedCard.css';
import type { ReactNode } from 'react';
import { IdCardIcon } from '@src/components/icons/IdCardIcon';
import { classes } from '@src/components/layout/space';
import './ParticipantTile.css';

export interface ParticipantTileProps {
  /** The face: a ParticipantFace, a SantaAvatar, a HiddenFace, or a photo. */
  avatar: ReactNode;
  name: string;
  /** One quiet line under the name — status, role, or context. */
  meta?: string;
  /** Card mark in the meta pill — identity screens, not people-grid status. */
  metaIcon?: boolean | ReactNode;
  /** Dim the avatar until the card has been opened. */
  muted?: boolean;
  decoration?: ParticipantDecorationVariant;
  edges?: false | DecoratedCardVariant;
  /** Legacy confirmation flag; status is displayed in the badge. */
  confirmed?: boolean;
  /** When set, the tile is a button. Omit for a static label. */
  onClick?: () => void;
  disabled?: boolean;
  /** Cap width when the tile sits alone on a screen, not in a grid. */
  standalone?: boolean;
  /** Compact card — claim and other identity screens. */
  plain?: boolean;
  /** Participant-list identity spacing, shared with access recovery. */
  listLayout?: boolean;
  className?: string;
  testId?: string;
}

/**
 * One participant as a tile: white card, portrait, name, meta pill.
 * Used in the people grid and anywhere a screen names a single card.
 */
export function ParticipantTile({
  avatar,
  name,
  meta,
  metaIcon = false,
  muted = false,
  decoration = 'forest',
  edges = 3,
  onClick,
  disabled = false,
  standalone = false,
  plain = false,
  listLayout = false,
  className,
  testId,
}: ParticipantTileProps) {
  return (
    <PersonCard
      layout="exchange"
      avatar={
        <span className={`participant-tile__art${muted ? ' participant-tile__art--muted' : ''}`}>
          {avatar}
        </span>
      }
      name={name}
      status={
        meta ? (
          <span className="participant-tile__meta">
            {metaIcon === true ? <IdCardIcon /> : metaIcon}
            {meta}
          </span>
        ) : undefined
      }
      decoration={decoration}
      edges={edges}
      onClick={disabled ? undefined : onClick}
      testId={testId}
      className={classes(
        className,
        standalone && 'participant-tile--standalone',
        plain && 'participant-tile--plain',
        listLayout && 'participant-tile--list',
      )}
    />
  );
}
